import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./HP_PlatformFeatures.css";
import shareImage from "../../../../img/icons/1_share_reviews.png";
import celebrateImage from "../../../../img/icons/2_celebrate.png";
import discoverImage from "../../../../img/icons/3_discover.png";
import moveMuseImage from "../../../../img/icons/4_move_n_muse.png";
import discoverHomeImage from "../../../../img/icons/DiscoverEventsHome.png";
import shareHomeImage from "../../../../img/icons/ShareReviewsHome.png";
import celebrateHomeImage from "../../../../img/icons/CelebrateDacersHome.png";
import blogHomeImage from "../../../../img/icons/BlogCommunityHome.png";

const HP_PlatformFeatures = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const sectionRef = useRef(null);

  const features = [
    {
      image: discoverImage,
      title: 'Discover Events',
      subtitle: 'Find Your Next Festival With Confidence',
      description: 'Browse real ratings on venue, safety, artists, ratio, and more - from dancers who\'ve actually been there.',
      expandedContent: [
        'Every year, thousands of dance events happen across the globe. How do you know which ones are worth your time, money, and travel? Move n Muse takes the guesswork out of it.',
        'Browse upcoming and past festivals freely - no account needed to start exploring. Filter by dance style, location, or ratings to find events that match your priorities. When you\'re ready, join the community to add events, contribute your own reviews, and help fellow dancers navigate the global festival landscape with real, honest insight.',
        'This isn\'t a listing site. It\'s the festival guide the social dance world has been missing.'
      ],
      link: '/festivals',
      cta: 'Browse Events',
      color: '#6B3A5B',
      expandedImage: discoverHomeImage
    },
    {
      image: shareImage,
      title: 'Share Honest Reviews',
      subtitle: 'Your Experience Is Valuable - And It\'s Protected',
      description: 'Your experience matters. Share honest event reviews anonymously to help fellow dancers choose wisely.',
      expandedContent: [
        'You\'ve been to multi-day dance events. You have opinions. But sharing them publicly in a tight-knit dance community? That\'s common practice which, unfortunately, can\'t serve the entire community.',
        'Move n Muse gives you a space to be honest without the social risk. All reviews are anonymous and moderated, so your feedback stays constructive and your identity stays safe.',
        'Rate events across six dimensions that go far beyond "it was fun" - from lead/follow ratio and venue quality to artist lineup, organization, culture, and safety. Share your perspective as an attendee, organizer, volunteer, artist, or invited guest, because the same festival looks very different depending on how you experienced it.',
        'Something didn\'t feel right? You can raise concerns too. Your honesty helps organizers improve, helps dancers make better choices, and helps the whole community hold a higher standard.',
        'Every piece of feedback - a glowing review, a critical note, a quiet flag - makes the dance world a little more transparent and a lot more trustworthy.'
      ],
      link: '/festivals',
      cta: 'Find Events',
      color: '#B8A898',
      expandedImage: shareHomeImage,
      imageScale: 1.5
    },
    {
      image: celebrateImage,
      title: 'Celebrate Fellow Dancers',
      subtitle: 'Give Kudos to the Dancers Who Made It Unforgettable',
      description: 'Give anonymous Kudos to the people who make our dancefloors kinder, safer, and more inspiring.',
      expandedContent: [
        'You know that dance. The one where a stranger met you with exactly the right energy - a long soft embrace with no agenda, a flawless sequence that left you breathless, joyful play and experimentation, unwavering presence and attention... or maybe simply a dance that reminded you why you started.',
        'Move n Muse lets you honor those moments. After confirming your attendance at an event, you can browse fellow attendees and send anonymous Kudos in five categories: Personality & Presence, Excellent Technique, Creativity & Play, Safe Space & Connection, and Other Magic - a space for whatever words you need to say.',
        'The more Kudos you receive, the more you can give - creating a cycle of recognition and generosity that rewards the people who make our dancefloors kinder, safer, and more alive.',
        'Missing your dance heroes on the platform? Spread the word. Every dancer deserves to know the impact they have.'
      ],
      link: '/festivals',
      cta: 'Find Events',
      color: '#C5CBA3',
      expandedImage: celebrateHomeImage,
      imageScale: 1.5
    },
    {
      image: moveMuseImage,
      title: 'Move n Muse Blog',
      subtitle: 'Where Movement Meets Meaning',
      description: 'Explore and contribute articles on dance culture, ethics, and the deeper side of movement.',
      expandedContent: [
        <><span className="platform-features__muse-word">Muse</span> (verb) - to think deeply, ponder, or contemplate something for a period, often while becoming absorbed in thought or dreamy abstraction.</>,
        'We at Move n Muse believe that Movement is only one side of the coin. Musing about the deeper layers - the relationships dance shapes, the boundaries it tests, the cultural histories it carries, the way it affects our confidence, our identity, our sense of belonging - this rarely gets the floor time it deserves.',
        'The Move n Muse Blog is a community-built library of social dance wisdom. Experienced instructors, educators, and practitioners contribute insights on cultural context, common misconceptions, technical fundamentals, and healthier community practices. And every dancer is invited to share their own reflections - the "I wish I\'d known" moments, the observations that helped them grow, the questions nobody asked in class.',
        'Every contribution is reviewed for alignment with our values: kindness, curiosity, respect, awareness, empowerment, and care. The result is a living, growing resource built by the community, for the community - one article and one conversation at a time.'
      ],
      link: '/blog',
      cta: 'Explore the Blog',
      color: '#D8C8E8',
      expandedImage: blogHomeImage
    }
  ];

  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
  };

  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Group features into rows of 4 (desktop) so expanded panel can sit below each row
  const rows = [];
  for (let i = 0; i < features.length; i += 4) {
    rows.push(features.slice(i, i + 4));
  }

  // Find which row contains the expanded card
  const expandedRowIndex = expandedIndex !== null ? Math.floor(expandedIndex / 4) : -1;
  const expandedFeature = expandedIndex !== null ? features[expandedIndex] : null;

  return (
    <section className="platform-features">
      <h2 ref={sectionRef} className="platform-features__title">
        What can you do on Move n Muse?
      </h2>

      {rows.map((row, rowIndex) => (
        <React.Fragment key={rowIndex}>
          <div className="platform-features__grid">
            {row.map((feature, colIndex) => {
              const globalIndex = rowIndex * 4 + colIndex;
              const isExpanded = expandedIndex === globalIndex;
              return (
                <div
                  key={globalIndex}
                  className={`platform-features__card ${isExpanded ? 'platform-features__card--active' : ''}`}
                  style={{ '--card-accent': feature.color, '--card-accent-rgb': hexToRgb(feature.color) }}
                  onClick={() => toggleExpanded(globalIndex)}
                >
                  <div className="platform-features__card-inner">
                    <div className="platform-features__image-wrapper">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="platform-features__image"
                      />
                    </div>
                    <h3 className="platform-features__title-text" style={{ color: feature.color }}>
                      <Link
                        to={feature.link}
                        className="platform-features__title-link"
                        style={{ color: feature.color }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {feature.title}
                      </Link>
                    </h3>
                    <p className="platform-features__desc">{feature.description}</p>
                  </div>
                  <span
                    className={`platform-features__dots ${isExpanded ? 'platform-features__dots--active' : ''}`}
                    style={{ color: feature.color }}
                  >
                    &bull;&bull;&bull;
                  </span>
                  <div
                    className="platform-features__indicator"
                    style={{ backgroundColor: feature.color }}
                  />
                </div>
              );
            })}
          </div>

          {/* Expanded panel below this row */}
          <div className={`platform-features__panel ${expandedRowIndex === rowIndex ? 'platform-features__panel--visible' : ''}`}>
            {expandedFeature && expandedRowIndex === rowIndex && (
              <div className="platform-features__panel-inner">
                <div className="platform-features__panel-text">
                  <h4
                    className="platform-features__panel-subtitle"
                    style={{ color: expandedFeature.color }}
                  >
                    {expandedFeature.subtitle}
                  </h4>
                  {expandedFeature.expandedContent.map((paragraph, pIndex) => (
                    <p key={pIndex} className="platform-features__panel-paragraph">
                      {paragraph}
                    </p>
                  ))}
                  <Link
                    to={expandedFeature.link}
                    className="platform-features__panel-cta"
                    style={{ backgroundColor: expandedFeature.color }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {expandedFeature.cta} &rarr;
                  </Link>
                </div>
                <div
                  className="platform-features__panel-icon"
                  style={expandedFeature.imageScale ? { width: `${Math.round(237 * expandedFeature.imageScale)}px` } : undefined}
                >
                  <img
                    src={expandedFeature.expandedImage}
                    alt={expandedFeature.title}
                    className="platform-features__panel-icon-img"
                  />
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </section>
  );
};

export default HP_PlatformFeatures;
